import {
  Directive,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { UserDirectoryService } from './user-directory.service';

/**
 * Profilkép a meglévő iniciálés avatar-elemekre.
 *
 * A támogatás, a munkaidő, a Mai nap és az üzenőfal mind ugyanazt a mintát
 * használja: színes körben a név kezdőbetűje. Ez a direktíva a NÉV alapján
 * megkeresi a felhasználó profilképét, és ha van, háttérképként ráteszi az
 * elemre — az iniciálé alatta marad, csak átlátszóvá válik, így kép nélküli
 * (vagy hibázó) esetben pontosan a régi megjelenés marad.
 *
 * Használat:  <span class="sup-avatar" [appUserAvatar]="name">{{ initial }}</span>
 */
@Directive({
  selector: '[appUserAvatar]',
})
export class UserAvatarDirective implements OnChanges, OnDestroy {
  /** A megjelenítendő felhasználó neve (teljes név vagy felhasználónév). */
  @Input() appUserAvatar: string | null = null;

  private subscription?: Subscription;
  private applied = false;

  constructor(
    private el: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private directory: UserDirectoryService,
  ) {}

  ngOnChanges(_changes: SimpleChanges): void {
    this.subscription?.unsubscribe();
    this.clear();

    const name = `${this.appUserAvatar || ''}`.trim();
    if (!name) {
      return;
    }
    this.subscription = this.directory
      .imageFileFor(name)
      .pipe(switchMap((imageFile) => this.directory.imageUrl(imageFile)))
      .subscribe((url) => {
        if (url) {
          this.apply(url);
        }
      });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private apply(url: string): void {
    const element = this.el.nativeElement;
    this.renderer.setStyle(element, 'background-image', `url("${url}")`);
    this.renderer.setStyle(element, 'background-size', 'cover');
    this.renderer.setStyle(element, 'background-position', 'center');
    // Az iniciálé a helyén marad (a méretezéshez kell), csak nem látszik.
    this.renderer.setStyle(element, 'color', 'transparent');
    this.applied = true;
  }

  private clear(): void {
    if (!this.applied) {
      return;
    }
    const element = this.el.nativeElement;
    this.renderer.removeStyle(element, 'background-image');
    this.renderer.removeStyle(element, 'background-size');
    this.renderer.removeStyle(element, 'background-position');
    this.renderer.removeStyle(element, 'color');
    this.applied = false;
  }
}
